import { PermanentServiceFragmentDoc, Shift, RevenueCalculationType, Site, PermanentServiceFragment, CarDetails, PurchaseDetails } from "@/graphql/__generated__/graphql";
import { Dispatch, JSX, SetStateAction, useState } from "react";
import { CustomToastComponent, IndicatorNumber, TextInfoGroupAlt } from "@/components/shared";
import { useIntl } from "react-intl";
import { formatNumberLocale, convertBpToDecimal, convertBpToDecimalOrUndefined, cleanUpRevenueComponentDataForSubmission, appendSiteAddress } from "@/utils/helpers";
import { useAddShiftMutationHook, useRemoveShiftMutationHook, useUpdateRevenueCalculationMutationHook } from "@/hooks/mutations";
import { useNavigate, useParams } from "react-router-dom";
import ShiftsComponent from "../../Shifts/ShiftsComponent";
import { merge } from "lodash";
import { TShiftSchema, ShiftDefaultValues, TEditPermanentServiceSchema, EditPermanentServiceSchema } from "../../Models";
import AddCarComponent from "../../AddCar/AddCarComponent";
import AddPurchaseComponent from "../../AddPurchase/AddPurchaseComponent";
import { Resolver, SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { RevenueCalculationTypeComponent } from "../../Components";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "react-toastify";
import { useFragment } from "@apollo/client/react";
import { SubmitAction } from "@/constants/enums";
import { CircleLoader } from "@/components/shared/loaders";

/**
 * Component to add or edit a permanent service and manage its shifts.
 * @returns {JSX.Element} - The AddPermanentService component.
 */
const EditPermanentService = ({ userCanEdit }: { userCanEdit: boolean }): JSX.Element => {

  // Hooks
  const intl = useIntl();

  const { t } = useTranslation(["translation", "common", "toasts", "modals"]);
  const navigate = useNavigate();
  const { tenderID: currentTenderID, serviceID } = useParams();

  //
  if (!currentTenderID || !serviceID) navigate(-1);

  // States
  const [showRemoveShiftAlert, setShowRemoveShiftAlert] = useState<boolean>(false);

  // Get existing Service data using useFragment (now includes cars and purchases with links)
  const existingService = useFragment({
    fragment: PermanentServiceFragmentDoc,
    from: { __typename: "PermanentService", id: serviceID },
  });

  // Get cars and purchases data from the service fragment
  const serviceCars = (existingService.data?.cars ?? []) as CarDetails[];
  const servicePurchases = (existingService.data?.purchases ?? []) as PurchaseDetails[];

  const onInvalid = (errors: unknown) => console.error(errors);

  const getDefaultValues = (service) => {
    const hourlyRateAbsolute = service.hourlyRateAbsolute ? {
      hourlyRateDayAbsolute: service.hourlyRateAbsolute.hourlyRateDayAbsolute,
      hourlyRateNightAbsolute: service.hourlyRateAbsolute.hourlyRateNightAbsolute,
      hourlyRateSundayAbsolute: service.hourlyRateAbsolute.hourlyRateSundayAbsolute,
      hourlyRateSundayNightAbsolute: service.hourlyRateAbsolute.hourlyRateSundayNightAbsolute,
      hourlyRateHolidayAbsolute: service.hourlyRateAbsolute.hourlyRateHolidayAbsolute,
      hourlyRateHolidayNightAbsolute: service.hourlyRateAbsolute.hourlyRateHolidayNightAbsolute,
    } : undefined;

    const hourlyRateRelative = service.hourlyRateRelative ? {
      hourlyRateDayAbsolute: service.hourlyRateRelative.hourlyRateDayAbsolute ?? 0,
      hourlyRateNightRelative: convertBpToDecimalOrUndefined(service.hourlyRateRelative.hourlyRateNightRelative) ?? 0,
      hourlyRateSundayRelative: convertBpToDecimalOrUndefined(service.hourlyRateRelative.hourlyRateSundayRelative) ?? 0,
      hourlyRateSundayNightRelative: convertBpToDecimalOrUndefined(service.hourlyRateRelative.hourlyRateSundayNightRelative) ?? 0,
      hourlyRateHolidayRelative: convertBpToDecimalOrUndefined(service.hourlyRateRelative.hourlyRateHolidayRelative) ?? 0,
      hourlyRateHolidayNightRelative: convertBpToDecimalOrUndefined(service.hourlyRateRelative.hourlyRateHolidayNightRelative) ?? 0,
    } : undefined;

    return {
      revenueCalculationType: service.revenueCalculationType ?? undefined,
      margin: service.revenueCalculationType === RevenueCalculationType.Margin ? convertBpToDecimal(service.margin) ?? undefined : undefined,
      flatRate: service.revenueCalculationType === RevenueCalculationType.Flatrate ? service.flatRate ?? undefined : undefined,
      hourlyRateAbsolute: service.revenueCalculationType === RevenueCalculationType.Hourlyrateabsolute ? hourlyRateAbsolute : undefined,
      hourlyRateRelative: service.revenueCalculationType === RevenueCalculationType.Hourlyraterelative ? hourlyRateRelative : undefined,
    };
  };

  const {
    control, watch, register, handleSubmit,
    setValue, trigger, reset, getValues, formState: { errors }
  } = useForm<TEditPermanentServiceSchema>({
    resolver: zodResolver(EditPermanentServiceSchema) as Resolver<TEditPermanentServiceSchema>,
    defaultValues: getDefaultValues(existingService.data),
    mode: "onChange"
  });

  // Refs and input fields data
  const inputFieldsData = [
    { name: "name", label: t("permanentservice.inputs.name.label"), type: "text" },
    { name: "operationalOrganizationShortName", label: t("permanentservice.inputs.operationalorganization.label") },
    { name: "hourlyWage", label: t("permanentservice.inputs.hourlywage.label"), type: "number", format: "currency" },
    { name: "nonTariffAllowance", label: t("permanentservice.inputs.nontariffallowance.label"), type: "number", format: "currency" },
    { name: "hoursBriefingPerEmployee", label: t("permanentservice.inputs.hoursbriefingperemployee.label"), type: "number" },
    { name: "transferredEmployeePercentage", label: t("permanentservice.inputs.transferredemployeepercentage.label"), type: "number", format: "percentage" },
    { name: "type", label: t("permanentservice.servicetype"), type: "text" },
    { name: "additional", label: t("permanentservice.inputs.additional.label"), format: "boolean" },
    { name: "pauseIncluded", label: t("permanentservice.inputs.pauseincluded.label"), format: "boolean" },
  ];

  // Mutation hooks
  const { addShiftMutation, loading: addShiftLoading } = useAddShiftMutationHook(Number(serviceID), Number(currentTenderID));
  const { removeShiftMutation, loading: removeShiftLoading } = useRemoveShiftMutationHook(Number(currentTenderID));
  const { updateRevenueCalculationMutation, loading: updateRevenueCalculationLoading } = useUpdateRevenueCalculationMutationHook();

  const isProcessing = addShiftLoading || removeShiftLoading;

  const revenueCalculationType = watch("revenueCalculationType");

  /**
   * Handler function for `removeShiftMutation`
   */
  const handleRemoveShift = async (shift: Shift) => {
    await removeShiftMutation({
      serviceId: Number(serviceID),
      shiftId: shift.id as number
    }, false);
  };

  /**
   * onSubmit handler for the updateMarginOnServiceMutation
   */
  const onSubmit: SubmitHandler<TEditPermanentServiceSchema> = (data: TEditPermanentServiceSchema): void => {
    // Clean up date for submission based on revenue calculation type
    cleanUpRevenueComponentDataForSubmission(data, revenueCalculationType);

    updateRevenueCalculationMutation({
      variables: {
        serviceId: Number(serviceID),
        input: data,
      },
    });
  };

  /**
   * Handler for the Add Shift button if an existing Service is being edited.
   * @param shiftData CreateShiftInput!
   */
  const handleAddShift = async (shiftData: TShiftSchema, action: SubmitAction, setShowShiftsModal: Dispatch<SetStateAction<boolean>>) => {
    try {
      // We merge the objects to add the fields that were not edited
      const newShiftObject = merge({}, ShiftDefaultValues, shiftData);

      await addShiftMutation({
        variables: {
          serviceId: Number(serviceID),
          input: newShiftObject,
        }
      }).then((res) => {
        // Only proceed if there are no errors
        if (!res.errors) {
          // Get current form values to preserve revenue calculation data
          const currentValues = getValues();

          // Reset only shift-related fields while preserving revenue calculation data
          reset({
            ...currentValues,
            ...ShiftDefaultValues
          });
          toast(CustomToastComponent, {
            toastId: "edit-permanent-service-shift-added-success",
            data: {
              title: t("modals:shift.toasts.addedsuccessfully"),
              type: "success",
            }
          });

          if (action === SubmitAction.AddAndClose) {
            setShowShiftsModal(false);
          }
        }
      });
    }
    catch (error) {
      console.error(error);

      toast(CustomToastComponent, {
        toastId: "edit-permanent-service-shift-added-error",
        data: {
          title: t("toasts:toasts.somethingwentwrong"),
          content: error.message || t("toasts:toasts.pleasetryagain"),
          type: "error",
        }
      });
    }
  };

  const handleEditShift = async (shiftData: TShiftSchema, shift: Shift, setShowEditShiftModal: Dispatch<SetStateAction<boolean>>) => {
    try {
      await removeShiftMutation({
        serviceId: Number(serviceID),
        shiftId: shift.id
      }, true).then(async () => {
        // We merge the objects to add the fields that were not edited
        const newShiftObject = merge({}, ShiftDefaultValues, shiftData);

        await addShiftMutation({
          variables: {
            serviceId: Number(serviceID),
            input: newShiftObject,
          },
        }).then((res) => {
          // Only proceed if there are no errors
          if (!res.errors) {
            toast(CustomToastComponent, {
              toastId: "edit-permanent-service-shift-edited-success",
              data: {
                title: t("modals:shift.toasts.editedsuccessfully"),
                type: "success",
              }
            });
            setShowEditShiftModal(false);
          }
        });
      });
    } catch (error) {
      toast(CustomToastComponent, {
        toastId: "edit-permanent-service-shift-edit-error",
        data: {
          title: t("toasts:toasts.somethingwentwrong"),
          content: error.message || t("toasts:toasts.pleasetryagain"),
          type: "error",
        }
      });
    }
  };

  const formatFieldValue = (fieldData, value) => {
    if (fieldData.format === "currency") {
      return formatNumberLocale(intl, value, "currency");
    } else if (fieldData.format === "boolean") {
      return t(value ? "common:yes" : "common:no");
    }
    else if (fieldData.format === "percentage") {
      return `${convertBpToDecimal(value)}%`;
    } else {
      return value;
    }
  };

  return (
    <div>
      <div className="flex-col-4">
        <div className="flex-wrap gap-4 flex mt-2">
          {inputFieldsData.map((fieldData) => {
            const value = existingService.data[(fieldData?.name)];
            return (
              <TextInfoGroupAlt
                key={fieldData.name}
                label={fieldData.label}
                content={formatFieldValue(fieldData, value)}
                className="min-w-[200px] max-w-[20%]"
              />
            );
          })}
        </div>

        {existingService.data.info && (
          <TextInfoGroupAlt
            label={t("permanentservice.inputs.info.label")}
            content={existingService.data.info}
          />
        )}

        <div className="flex flex-row gap-2 items-end">
          {/*  Site ID input */}
          {
            <TextInfoGroupAlt
              label={t("permanentservice.inputs.site.label")}
              content={`${existingService.data?.site?.name} - ${appendSiteAddress(existingService.data.site as Site)}`}
              className="w-full md:min-w-[49%] md:flex-1"
            />
          }
        </div>
        <Card className="p-4">
          <form id="update-revenue-calculation" onSubmit={handleSubmit(onSubmit, onInvalid)}>
            <div className="flex-col-4">
              <div className="flex-col-4">
                {/* Revenue Calculation Type */}
                <RevenueCalculationTypeComponent
                  control={control}
                  errors={errors}
                  setValue={setValue}
                  register={register}
                  watch={watch}
                  trigger={trigger}
                  service={existingService.data as PermanentServiceFragment}
                  disabled={!userCanEdit}
                  defaultValues={getDefaultValues(existingService.data)}
                >
                  {
                    userCanEdit ? (
                      <div className="flex justify-end">
                        <Button
                          type="submit"
                          disabled={updateRevenueCalculationLoading}
                          className="gap-2"
                        >
                          <CircleLoader visible={updateRevenueCalculationLoading} height={16} width={16} strokeWidth={2} />
                          {t("common:buttons.save")}
                        </Button>
                      </div>
                    ) : null
                  }
                </RevenueCalculationTypeComponent>
              </div>
            </div>
          </form>
        </Card>
      </div>

      {/* Shifts Section */}
      <ShiftsComponent
        userCanEdit={userCanEdit}
        onAddShift={handleAddShift}
        onEditShift={handleEditShift}
        shiftsToDisplay={existingService.data.shifts as Shift[]}
        onRemoveShift={handleRemoveShift}
        setShowRemoveShiftAlert={setShowRemoveShiftAlert} showRemoveShiftAlert={showRemoveShiftAlert}
        isProcessing={isProcessing}
      />

      {/* Linked Cars Section (read-only, managed at Tender level) */}
      <div>
        <div className="flex items-end justify-between my-4">
          <div className="flex items-center gap-2 h-[40px]">
            <h2 className="text-lg font-semibold">
              {t("linkedcars.title")}
            </h2>
            <IndicatorNumber number={serviceCars.length} />
          </div>
        </div>
        <AddCarComponent cars={serviceCars} />
      </div>

      {/* Linked Purchases Section (read-only, managed at Tender level) */}
      <div>
        <div className="flex items-end justify-between my-4">
          <div className="flex items-center gap-2 h-[40px]">
            <h2 className="text-lg font-semibold">
              {t("linkedpurchases.title")}
            </h2>
            <IndicatorNumber number={servicePurchases.length} />
          </div>
        </div>
        <AddPurchaseComponent purchases={servicePurchases} />
      </div>
    </div>
  );
};

export default EditPermanentService;
